<script setup>
// Construtor de condição estruturado (profissional): regras [campo, operador,
// valor] combinadas por E/OU + os dois caminhos (Se sim / Se não). Compilado na
// instrução do agente (ApiService#flow_instructions_from_workflow).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({ data: { type: Object, required: true } });
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();

const FIELDS = [
  'intent',
  'message',
  'contact_name',
  'contact_email',
  'contact_phone',
  'custom',
];
const OPERATORS = ['eq', 'neq', 'contains', 'not_contains', 'present', 'blank'];
// operadores que não precisam de valor
const NO_VALUE = ['present', 'blank'];

const rules = computed(() =>
  props.data.conditionRules?.length
    ? props.data.conditionRules
    : [{ field: 'intent', op: 'eq', value: '' }]
);
const matchMode = computed(() => props.data.matchMode || 'all');

const commit = list => emit('update', { conditionRules: list });
const updateRule = (idx, key, value) =>
  commit(rules.value.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
const addRule = () =>
  commit([...rules.value, { field: 'message', op: 'contains', value: '' }]);
const removeRule = idx => commit(rules.value.filter((_, i) => i !== idx));
const needsValue = op => !NO_VALUE.includes(op);
const needsKey = field => field === 'custom';
const onField = (key, event) => emit('update', { [key]: event.target.value });
</script>

<template>
  <div class="p-4">
    <div class="mb-1 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <span class="i-lucide-git-fork text-n-slate-11" />
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ data.label || t('AI_AGENTS.FLOW.IFELSE.TITLE') }}
        </h3>
      </div>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>
    <p class="mb-3 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.IFELSE.SUBTITLE') }}
    </p>

    <!-- E / OU -->
    <div class="mb-2 flex items-center gap-2">
      <span class="text-xs text-n-slate-10">{{
        t('AI_AGENTS.FLOW.IFELSE.MATCH')
      }}</span>
      <select
        :value="matchMode"
        class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1 text-xs"
        @change="onField('matchMode', $event)"
      >
        <option value="all">{{ t('AI_AGENTS.FLOW.IFELSE.MATCH_ALL') }}</option>
        <option value="any">{{ t('AI_AGENTS.FLOW.IFELSE.MATCH_ANY') }}</option>
      </select>
    </div>

    <!-- Regras -->
    <div class="space-y-2">
      <div
        v-for="(rule, idx) in rules"
        :key="idx"
        class="rounded-lg border border-n-weak p-2"
      >
        <div class="mb-1 flex items-center gap-1">
          <select
            :value="rule.field"
            class="min-w-0 flex-1 rounded-md border border-n-weak bg-n-solid-1 px-1.5 py-1 text-xs"
            @change="updateRule(idx, 'field', $event.target.value)"
          >
            <option v-for="f in FIELDS" :key="f" :value="f">
              {{ t(`AI_AGENTS.FLOW.IFELSE.FIELDS.${f}`) }}
            </option>
          </select>
          <select
            :value="rule.op"
            class="min-w-0 flex-1 rounded-md border border-n-weak bg-n-solid-1 px-1.5 py-1 text-xs"
            @change="updateRule(idx, 'op', $event.target.value)"
          >
            <option v-for="o in OPERATORS" :key="o" :value="o">
              {{ t(`AI_AGENTS.FLOW.IFELSE.OPS.${o}`) }}
            </option>
          </select>
          <button
            v-if="rules.length > 1"
            class="i-lucide-trash-2 shrink-0 text-n-ruby-9"
            @click="removeRule(idx)"
          />
        </div>
        <input
          v-if="needsKey(rule.field)"
          :value="rule.key"
          :placeholder="t('AI_AGENTS.FLOW.IFELSE.KEY_PH')"
          class="mb-1 w-full rounded-md border border-n-weak bg-n-solid-1 px-2 py-1 text-xs"
          @input="updateRule(idx, 'key', $event.target.value)"
        />
        <input
          v-if="needsValue(rule.op)"
          :value="rule.value"
          :placeholder="t('AI_AGENTS.FLOW.IFELSE.VALUE_PH')"
          class="w-full rounded-md border border-n-weak bg-n-solid-1 px-2 py-1 text-xs"
          @input="updateRule(idx, 'value', $event.target.value)"
        />
      </div>
    </div>
    <button
      class="mt-2 flex items-center gap-1 text-xs font-medium text-n-brand hover:underline"
      @click="addRule"
    >
      <span class="i-lucide-plus" /> {{ t('AI_AGENTS.FLOW.IFELSE.ADD_RULE') }}
    </button>

    <!-- Ramos -->
    <div class="mt-3 grid grid-cols-2 gap-2">
      <div>
        <label class="mb-1 block text-xs text-n-teal-11">
          {{ t('AI_AGENTS.FLOW.IFELSE.TRUE_LABEL') }}
        </label>
        <input
          :value="data.trueLabel"
          :placeholder="t('AI_AGENTS.FLOW.IFELSE.TRUE_PH')"
          class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1 text-sm"
          @input="onField('trueLabel', $event)"
        />
      </div>
      <div>
        <label class="mb-1 block text-xs text-n-ruby-11">
          {{ t('AI_AGENTS.FLOW.IFELSE.FALSE_LABEL') }}
        </label>
        <input
          :value="data.falseLabel"
          :placeholder="t('AI_AGENTS.FLOW.IFELSE.FALSE_PH')"
          class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1 text-sm"
          @input="onField('falseLabel', $event)"
        />
      </div>
    </div>
    <p class="mt-2 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.IFELSE.HINT') }}
    </p>

    <button
      class="mt-4 w-full rounded-lg border border-n-ruby-7 py-2 text-sm font-medium text-n-ruby-9 hover:bg-n-ruby-3"
      @click="emit('remove')"
    >
      {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
    </button>
  </div>
</template>
